// Drag a match from Matches onto a map (Ben, 2026-10-18): press a match's minutes bar and move. It
// looks like a player drag: a Shot Map, Pass Map, Heat Map or Passing Profile gets the blue outline
// (drop → that module shows only that match, switching to the dragged player if it showed another),
// anything else red (no drop).
// Also zones from a Pass Map onto a Heat Map (Ben, 2026-10-19), the same drag with a "zone" focus.
// One Undo step. Its own small drag, not the add-to-canvas one (that places modules on the grid).

import { MATCH_DROP_TITLES, setModuleFocus, type EventFocus } from "../live/event-focus.js";
import { pixelSize, playerIdFromSelect } from "./module-shell.js";
import { absorb, setModulePlayer } from "./drop-actions.js";
import { ensurePlayerOption } from "./player-options.js";
import { getModState, setModState } from "../modules/bridge.js";
import { getDefaultSize } from "../modules/module-config.js";
import { pickedPlayer } from "../live/picked-players.js";
import { getScale as scale } from "./zoom.js";
import { canvasApi } from "./shell-actions.js";
import { undoApi } from "./undo.js";
import { hideTooltip } from "./tooltip.js";
import type { MatchRef } from "../live/match.js";
import { showMatchOnMap } from "../modules/TeamMap.js";

const START_PX = 5;

/** Would a module take a dragged match / position? Any of the four maps; one showing another
 *  player switches to the dragged one (Ben, 2026-10-18: drag Bruno G's minutes onto Rice's Pass Map). */
export function takesMatch(title: string): boolean {
  return MATCH_DROP_TITLES.has(title);
}

function targetAt(x: number, y: number, ghost: HTMLElement): HTMLElement | null {
  ghost.style.display = "none";
  const el = document.elementFromPoint(x, y);
  ghost.style.display = "";
  return el?.closest<HTMLElement>(".placed-module") ?? null;
}

function titleOf(mod: HTMLElement): string {
  return mod.querySelector(".module-title")?.textContent?.trim() ?? "";
}

const PASS_SET_TITLES: ReadonlySet<string> = new Set(["Pass Map", "Passing Profile", "Heat Map"]);

/** Would a module take this focus? Zones from a Pass Map go to a Heat Map only (Ben, 2026-10-19:
 *  "where did those passes go"); a match / position to any of the four maps. */
function verdict(mod: HTMLElement, focus: EventFocus): boolean {
  if (focus.kind === "zone") return titleOf(mod) === "Heat Map";
  // A set of passes from the Passing Report: any map that shows passes (Ben, 2026-10-20).
  if (focus.kind === "passes") return PASS_SET_TITLES.has(titleOf(mod));
  return takesMatch(titleOf(mod));
}

function playerOf(mod: HTMLElement): number | null {
  const select = mod.querySelector<HTMLSelectElement>(".player-select");
  return select && select.value !== "" ? playerIdFromSelect(select.value) : null;
}

/** The drop: one Undo step. The map moves to the dragged league + season (Ben, 2026-10-18: a match
 *  from another season must work too), switching to the dragged player if it showed another; then
 *  it narrows to the match / position. */
function dropOn(target: HTMLElement, focus: EventFocus): void {
  const undo = undoApi();
  undo.saveState();
  // A game from a player's Matches on a map that shows matches: the match, the player's chip on.
  if (focus.kind === "match" && focus.match && MATCH_VIEW_TITLES.has(titleOf(target))) {
    const { match, playerSide, playerName } = focus;
    undo.withCheckpointHeld(() => {
      showMatchOnMap(target, match, { side: playerSide, player: { id: focus.playerId, name: playerName ?? "" } });
      canvasApi().refreshModuleBody(target);
    });
    return;
  }
  undo.withCheckpointHeld(() => {
    setModuleFocus(target, focus);
    // Where the map opens (useLiveEventMap reads these for the player they name).
    setModState(target, "_eventMapPlayer", focus.playerId);
    setModState(target, "_eventMapCompetitionId", String(focus.competitionId));
    setModState(target, "_eventMapSeason", focus.seasonCode);
    // Zones on a Heat Map: show where those passes ENDED (Catalyst's "Selected Pass Ends" pitch).
    if (focus.kind === "zone") setModState(target, "_heatmapAction", "passDestinations");
    // A set of passes on a Heat Map: where they started.
    if (focus.kind === "passes") setModState(target, "_heatmapAction", "passOrigins");
    if (playerOf(target) === focus.playerId) {
      canvasApi().refreshModuleBody(target);
      return;
    }
    const select = target.querySelector<HTMLSelectElement>(".player-select");
    const picked = pickedPlayer(focus.playerId);
    if (select && picked) ensurePlayerOption(select, picked);
    setModulePlayer(target, focus.playerId);
  });
}

/** The maps that show a dropped match itself, a team at a time (Ben, 2026-10-30: "If I swap a player
 *  with a match, the player is irrelevant"). The other maps wait for their team view. */
const MATCH_VIEW_TITLES: ReadonlySet<string> = new Set(["Shot Map", "Pass Map"]);

/** Does this module take a dropped match (a Match's game, a result's score)? */
export function narrowsToMatch(mod: HTMLElement | null): boolean {
  return !!mod && MATCH_VIEW_TITLES.has(titleOf(mod));
}

/** A dropped match on a map (teams and matches, Ben 2026-10-30: "the module is the match"): the
 *  map keeps its player and narrows to that game, in its league and season. A player who didn't
 *  play in it shows the map's own no-data message. The caller holds the Undo step (the drag's). */
export function narrowMapToMatch(target: HTMLElement, match: MatchRef): void {
  if (!narrowsToMatch(target)) throw new Error("narrowMapToMatch: this map doesn't show a match");
  showMatchOnMap(target, match);
  canvasApi().refreshModuleBody(target);
}

/** Would a module take this player's game (a bar from a player's Matches)? Any of the maps; also
 *  read by the Match drag that carries it (menu-drag). */
export function takesMatchFocus(mod: HTMLElement | null, focus: EventFocus | null): boolean {
  return !!mod && !!focus && verdict(mod, focus);
}

/** Drop a player's game on a map (one Undo step; inside a held checkpoint, none of its own). */
export function dropMatchFocus(target: HTMLElement, focus: EventFocus): void {
  dropOn(target, focus);
}

/** A whole Match module dropped on another module (Ben, 2026-10-31: "I would just drag the whole
 *  module on to a different module"): a Shot Map / Pass Map shows its game, a Match switches to
 *  it. Null for anything else (the drop rules decide). */
export function matchModuleDrop(source: HTMLElement, target: HTMLElement): "show-match" | null {
  if (source === target || titleOf(source) !== "Match" || getModState(source, "_match") === undefined) return null;
  return narrowsToMatch(target) || titleOf(target) === "Match" ? "show-match" : null;
}

/** The drop: the target shows the Match module's game, and the Match fades into it, as a player's
 *  module does onto a map. Holds no Undo step: the caller does. */
export function showMatchFromModule(source: HTMLElement, target: HTMLElement): void {
  const match = getModState<MatchRef>(source, "_match");
  if (!match) throw new Error("showMatchFromModule: the Match shows no game");
  if (titleOf(target) === "Match") {
    setModState(target, "_match", match);
    canvasApi().refreshModuleBody(target);
  } else {
    narrowMapToMatch(target, match);
  }
  absorb(source);
}

/** Start (after a few pixels of movement) dragging `focus` from a pointerdown on a match bar. It looks
 *  exactly like a player drag (Ben, 2026-10-18): the same ghost, and the same outline on the module
 *  under it — blue when it takes the match, red when it doesn't. */
export function startMatchDrag(e: PointerEvent, source: HTMLElement, focus: EventFocus): void {
  const x0 = e.clientX;
  const y0 = e.clientY;
  const indicator = document.getElementById("snap-indicator");
  if (!indicator) throw new Error("#snap-indicator missing from legacy-scaffold.html");
  // A player token's size (menu-drag's startPlayerTokenDrag: the Bio's), until it's over a module.
  const bio = getDefaultSize("Bio");
  const baseW = pixelSize(bio.cols);
  const baseH = pixelSize(bio.rows);
  let ghost: HTMLElement | null = null;
  let over: HTMLElement | null = null;
  let ok = false;

  const place = (ev: PointerEvent, g: HTMLElement) => {
    const s = scale();
    over = targetAt(ev.clientX, ev.clientY, g);
    if (over === source) over = null;
    ok = over !== null && verdict(over, focus);
    // Over a module: the ghost takes its size and the outline sits on it (blue / red), as a merge.
    const w = over ? Number.parseInt(over.style.width, 10) || baseW : baseW;
    const h = over ? Number.parseInt(over.style.height, 10) || baseH : baseH;
    g.style.width = `${w}px`;
    g.style.height = `${h}px`;
    g.style.left = `${ev.clientX - (w * s) / 2}px`;
    g.style.top = `${ev.clientY - (h * s) / 2}px`;
    if (!over) {
      indicator.style.display = "none";
      indicator.classList.remove("merge", "invalid");
      return;
    }
    indicator.style.display = "block";
    indicator.style.width = `${w}px`;
    indicator.style.height = `${h}px`;
    indicator.style.left = over.style.left;
    indicator.style.top = over.style.top;
    indicator.classList.toggle("merge", ok);
    indicator.classList.toggle("invalid", !ok);
  };
  const onMove = (ev: PointerEvent) => {
    if (!ghost) {
      if (Math.hypot(ev.clientX - x0, ev.clientY - y0) < START_PX) return;
      hideTooltip();
      document.body.classList.add("is-dragging-module");
      ghost = document.createElement("div");
      ghost.className = "dragging-ghost";
      ghost.style.transformOrigin = "center center";
      ghost.style.transform = `scale(${scale()})`;
      ghost.style.opacity = "0.95";
      document.body.appendChild(ghost);
    }
    place(ev, ghost);
  };
  const end = (drop: boolean) => {
    window.removeEventListener("pointermove", onMove);
    window.removeEventListener("pointerup", onUp);
    window.removeEventListener("pointercancel", onCancel);
    ghost?.remove();
    indicator.style.display = "none";
    indicator.classList.remove("merge", "invalid");
    document.body.classList.remove("is-dragging-module");
    if (drop && over && ok) dropOn(over, focus);
  };
  const onUp = () => end(true);
  const onCancel = () => end(false);
  window.addEventListener("pointermove", onMove);
  window.addEventListener("pointerup", onUp);
  window.addEventListener("pointercancel", onCancel);
}
